<template>
  <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
    <div
      class="card-header bg-secondary text-white d-flex justify-content-between align-items-center"
    >
      <h6 class="mb-0"><i class="fa fa-money me-2"></i>Refund Tracking</h6>
      <span class="badge" :class="statusBadgeClass">{{ refundStatus }}</span>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="text-center py-2">
        <span class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading refund status…</span>
        </span>
      </div>
      <div
        v-else-if="loadError"
        class="alert alert-danger outline small d-flex align-items-center mb-0"
      >
        <span class="flex-grow-1">{{ loadError }}</span>
        <button class="btn btn-sm btn-outline-danger ms-2" @click="fetchRefund">
          <i class="fa fa-refresh me-1"></i>Retry
        </button>
      </div>
      <template v-else-if="refundData && refundData.status !== 'none'">
        <dl class="row mb-2 small">
          <dt class="col-sm-4">PSP Platform</dt>
          <dd class="col-sm-8">{{ refundData.pspPlatform || '—' }}</dd>
          <dt class="col-sm-4">Status</dt>
          <dd class="col-sm-8">{{ refundData.status }}</dd>
          <dt class="col-sm-4">Last Updated</dt>
          <dd class="col-sm-8">
            <ZonedDateTime :value="refundData.updatedAt" :zone="timezone" :kind="timezoneKind" />
          </dd>
          <dt class="col-sm-4">Updated By</dt>
          <dd class="col-sm-8">{{ refundData.updatedBy || '—' }}</dd>
        </dl>
        <!-- Comments thread -->
        <div v-if="refundData.comments && refundData.comments.length" class="mb-2">
          <strong class="small">Comments:</strong>
          <div
            v-for="(c, idx) in refundData.comments"
            :key="idx"
            class="border-start border-2 ps-2 mb-1 small"
          >
            <ZonedDateTime
              class="text-muted"
              :value="c.timestamp"
              :zone="timezone"
              :kind="timezoneKind"
              layout="inline"
            />
            <span class="ms-1">— {{ c.author }}</span>
            <div>{{ c.text }}</div>
          </div>
        </div>
        <div
          v-if="refundData.status !== 'refunded' && refundData.status !== 'rejected'"
          class="alert alert-warning outline small mb-0"
        >
          <i class="fa fa-info-circle me-1"></i>
          Refund pending — manual PSP action required.
        </div>
      </template>
      <p v-else class="text-muted small mb-0">No refund tracking record for this order.</p>

      <!-- Edit form (agents/admins only) -->
      <template v-if="canEdit">
        <hr />
        <div class="small">
          <div class="row mb-2">
            <div class="col-md-6">
              <label class="form-label mb-1">PSP Platform</label>
              <select
                v-model="editForm.pspPlatform"
                class="form-select border border-primary form-select-sm"
              >
                <option value="PayPal">PayPal</option>
                <option value="Amazon Pay">Amazon Pay</option>
                <option value="Stripe">Stripe</option>
                <option value="Braintree">Braintree</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label mb-1">Status</label>
              <select
                v-model="editForm.status"
                class="form-select border border-primary form-select-sm"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="refunded">Refunded</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div class="mb-2">
            <label class="form-label mb-1">Comment</label>
            <textarea
              v-model="editForm.comment"
              class="form-control border border-primary form-control-sm"
              rows="2"
              placeholder="Add a comment..."
            ></textarea>
          </div>
          <button class="btn btn-sm btn-primary" :disabled="isSaving" @click="saveRefund">
            <span v-if="isSaving"
              ><span class="spinner-border spinner-border-sm me-1"></span>Saving...</span
            >
            <span v-else><i class="fa fa-save me-1"></i>Update Refund</span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';

const props = defineProps({
  orderUuid: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
  timezone: { type: String, default: 'UTC' },
  timezoneKind: { type: String, default: 'user' },
});

const toast = useToast();
const refundData = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref('');
const editForm = ref({ pspPlatform: 'PayPal', status: 'pending', comment: '' });

const refundStatus = computed(() => refundData.value?.status || 'none');
const statusBadgeClass = computed(() => {
  switch (refundStatus.value) {
    case 'refunded':
      return 'bg-success';
    case 'in_progress':
      return 'bg-info';
    case 'pending':
      return 'bg-warning text-dark';
    case 'rejected':
      return 'bg-danger';
    default:
      return 'bg-secondary';
  }
});

async function fetchRefund() {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.refund(props.orderUuid),
    );
    refundData.value = data;
  } catch (err) {
    refundData.value = null;
    loadError.value =
      err?.response?.data?.message || 'Failed to load the refund status. Try again.';
  } finally {
    isLoading.value = false;
  }
}

async function saveRefund() {
  isSaving.value = true;
  try {
    await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.refund(props.orderUuid),
      editForm.value,
    );
    toast.success('Refund tracking updated.');
    await fetchRefund();
    editForm.value.comment = '';
  } catch (err) {
    toast.error(err?.response?.data?.message || 'Failed to update refund.');
  } finally {
    isSaving.value = false;
  }
}

onMounted(fetchRefund);
watch(() => props.orderUuid, fetchRefund);
</script>
